// Organization API fns — carved verbatim from `src/api/scribeAPIV2.ts`
// (which now re-exports from here). Transport comes from the `@heidi/api`
// configured-client registry (E5, ARCFE-805) — the registry hands back the
// app's real `scribeHttpV2` singleton (same interceptors, same retry/refresh
// state), so behaviour is byte-identical. `WorkspaceHttpClient` keeps the
// public signatures structural (no axios types in this package's surface).

import { getScribeHttpClients } from '@heidi/api/client';
import {
  type components,
  type OrganizationMemberOrdering,
} from '@heidi/schema/ml-scribe';
import {
  type EligibleCountResponse,
  type GetRolesResponse,
  type InviteUsersToOrgTeamResponse,
  type InviteUsersToTeamRequest,
  type JoinedOrganizationsResponse,
  type LogoutAllMatchingResponse,
  type LogoutJobStatus,
  type LogoutMemberFilter,
  type LogoutMembersRequest,
  type LogoutMembersResponse,
  type MoveOrganizationMemberTeamParams,
  type OrganizationMembersResponse,
  type OrganizationMembersView,
  type OrganizationTeamsResponse,
  type OrganizationWithTeamsResponse,
  type SessionTimeoutInfo,
  type UpdateOrganizationRequest,
  type UpdateTeamMemberRequest,
  type UpdateUserRolesRequest,
  type UpdateUserRolesResponse,
} from '../../contracts';
import { type WorkspaceHttpClient } from '../config';

const http = (): WorkspaceHttpClient => getScribeHttpClients().scribeHttpV2;

export const searchOrganizationMembers = (params?: { q?: string; limit?: number }) => {
  return http().get<components['schemas']['SearchMembersResponse']>(
    '/organization/members/search',
    { params }
  );
};

export const getOrganizationTeams = async (
  organizationId: string
): Promise<OrganizationTeamsResponse> => {
  try {
    const { data } = await http().get<OrganizationTeamsResponse>(
      `/organization/${organizationId}/teams`
    );
    return data;
  } catch (error: unknown) {
    if ((error as { response?: { status?: number } })?.response?.status === 403) {
      return { teams: [] };
    }
    throw error;
  }
};

// --- OT2b (ARCFE-940): the organization/* route cohort ---------------------
// Carved verbatim from `src/api/scribeAPIV2.ts` (which re-exports name-intact),
// completing the OT2 pair after the teams cohort (#17714). Includes the three
// org-nested team-member routes, `InviteUsersToTeams` + `updateOrganizationMemberRole`
// (ratified -> org-teams per design D2/P0.4), and the logout-everywhere cluster
// (note its camelCase-vs-snake_case contract split, pinned in the tests) plus
// `getLogoutEligibleCount` — workspace-tagged but invisible to the symbol-pattern
// census (no Organization/Team noun in the name; recorded with P0.7).

/** @domain workspace */
export const getJoinedOrganization = () => {
  return http().get<JoinedOrganizationsResponse>(`/organization`);
};

/** @domain workspace */
export const getOrganizationByOrganizationId = (organizationId: string) => {
  return http().get<OrganizationWithTeamsResponse>(`/organization/${organizationId}`);
};

/** @domain workspace */
export const updateOrganizationById = (
  organizationId: string,
  updateOrganizationRequest: UpdateOrganizationRequest
) => {
  return http().patch<OrganizationWithTeamsResponse>(
    `/organization/${organizationId}`,
    updateOrganizationRequest
  );
};

/**
 * MUL-1879: set the org-level session-inactivity timeout (org admin only).
 * Returns the stored {@link SessionTimeoutInfo} (minutes/set_by/scope).
 * @domain workspace
 */
export const setOrganizationSessionTimeout = (
  organizationId: string,
  minutes: number
) => {
  return http().patch<SessionTimeoutInfo>(
    `/organization/${organizationId}/session-timeout`,
    { minutes }
  );
};

/**
 * MUL-1879: remove the org-level session-inactivity timeout (org admin only).
 * Idempotent — clearing an org with no stored value still returns 204.
 * After clearing, members fall through to their own user-level override or
 * the cohort default at resolve time.
 * @domain workspace
 */
export const clearOrganizationSessionTimeout = (organizationId: string) => {
  return http().delete<void>(`/organization/${organizationId}/session-timeout`);
};

/** @domain workspace */
export const getOrganizationMembers = (
  organizationId: string,
  // TODO: add type for input from schema
  input: {
    page?: number;
    pageSize?: number;
    orderBy?: OrganizationMemberOrdering;
    isDescending?: boolean;
    search?: string;
    teamIds?: string[];
    roleIds?: string[];
    specialtyIds?: string[];
    showPendingInvitationsOnly?: boolean;
  }
) => {
  return http().get<OrganizationMembersResponse>(
    `/organization/${organizationId}/members`,
    {
      params: {
        page_size: input.pageSize,
        page: input.page,
        order_by: input.orderBy,
        is_descending: input.isDescending,
        search: input.search,
        team_ids: input.teamIds,
        role_ids: input.roleIds,
        specialty_ids: input.specialtyIds,
        show_pending_invitations_only: input.showPendingInvitationsOnly,
      },
    }
  );
};

/** @domain workspace */
export const updateOrganizationMemberRole = (
  organizationId: string,
  teamId: string,
  memberKindeId: string,
  UpdateTeamMemberRequest: UpdateTeamMemberRequest
) => {
  return http().patch<OrganizationMembersView>(
    `/organization/${organizationId}/teams/${teamId}/members/${memberKindeId}`,
    UpdateTeamMemberRequest
  );
};

/** @domain workspace */
export const deleteOrganizationTeamMember = (
  organizationId: string,
  teamId: string,
  memberId: string
) => {
  return http().delete(
    `/organization/${organizationId}/teams/${teamId}/members/${memberId}`
  );
};

/** @domain workspace */
export const moveOrganizationMemberTeam = ({
  organizationId,
  sourceTeamId,
  memberKindeId,
  moveMemberToTeamRequest,
}: MoveOrganizationMemberTeamParams) => {
  return http().post<OrganizationMembersView>(
    `/organization/${organizationId}/teams/${sourceTeamId}/members/${memberKindeId}/move`,
    moveMemberToTeamRequest
  );
};

/** @domain workspace */
export const InviteUsersToTeams = (
  organizationId: string,
  input: InviteUsersToTeamRequest
) => {
  return http().post<InviteUsersToOrgTeamResponse>(
    `/organization/${organizationId}/invitations`,
    input
  );
};

/** @domain workspace */
export const logoutOrganizationMembersEverywhere = (
  organizationId: string,
  input: LogoutMembersRequest
) => {
  return http().post<LogoutMembersResponse>(
    `/organization/${organizationId}/users/logout-everywhere`,
    input
  );
};

// Server-resolved "log out all matching members" flow. Filter params/body use
// camelCase (the legacy /members list uses snake_case — intentionally different
// per the backend contract). The job-status endpoint returns snake_case.
// `excludedUserRefIds` removes specific members from the filter-matched set —
// used when the admin is in "select all" mode but has deselected a few rows, so
// the org-wide selection survives the opt-outs instead of collapsing to the
// current page. This is the exact field name the backend expects; omitted from
// the request when empty.
/** @domain workspace */
export const getLogoutEligibleCount = (
  organizationId: string,
  filter: LogoutMemberFilter,
  excludedUserRefIds?: string[]
) => {
  return http().get<EligibleCountResponse>(
    `/organization/${organizationId}/users/logout-everywhere/eligible-count`,
    {
      params: {
        search: filter.search,
        teamIds: filter.teamIds,
        roleIds: filter.roleIds,
        specialtyIds: filter.specialtyIds,
        excludedUserRefIds: excludedUserRefIds?.length ? excludedUserRefIds : undefined,
      },
    }
  );
};

/** @domain workspace */
export const logoutAllMatchingOrganizationMembers = (
  organizationId: string,
  filter: LogoutMemberFilter,
  excludedUserRefIds?: string[]
) => {
  return http().post<LogoutAllMatchingResponse>(
    `/organization/${organizationId}/users/logout-everywhere/all-matching`,
    {
      ...filter,
      excludedUserRefIds: excludedUserRefIds?.length ? excludedUserRefIds : undefined,
    }
  );
};

/** @domain workspace */
export const getOrganizationLogoutJob = (organizationId: string, jobId: string) => {
  return http().get<LogoutJobStatus>(
    `/organization/${organizationId}/users/logout-everywhere/jobs/${jobId}`
  );
};

/** Carved with OT3 slice 2 (ARCFE-955) — the roles catalog read the roles
 * hooks gate on. Route + params verbatim from the hub. */
export const getRoles = (teamId?: string) => {
  return http().get<GetRolesResponse>(`/roles`, {
    params: {
      team_id: teamId,
    },
  });
};

/** Carved with IP3 (ARCFE-970) — the role-management write, org-teams per the
 * P0.4 ruling (route `roles`, same family as the `getRoles` catalog read).
 * Route + verb verbatim from the hub. */
export const updateUserRole = (input: UpdateUserRolesRequest) => {
  return http().patch<UpdateUserRolesResponse>(`/roles`, input);
};

/**
 * `GET /organization/{organizationId}/analytics-dashboard` — the signed
 * dashboard URL `useQueryOrgAnalyticsDashboard` renders behind.
 *
 * THE HEAL PATH THE ARCFE-1060 DOWNGRADE NAMED, executed. That pass widened the
 * family's `apiHubs` arm to spell `OrgAnalytics` — the symbol abbreviates the
 * noun, so `[Oo]rganization` had never matched it — and downgraded `api` from
 * Green precisely because the newly-visible endpoint was a LIVE app-side
 * implementation rather than one of the two P0.4-deferred session-typed views
 * the Green rested on. Carving it removes the reason for the downgrade.
 *
 * The response type stays the inline `{ url: string }` the hub declared: it is
 * not a schema pick and hoisting it into `/contracts` would mint a contract for
 * one caller. The sibling `getTeamAnalyticsDashboard` in `teams.ts` declares the
 * same shape inline for the same reason.
 *
 * NOTE THE GRAIN THAT DOES NOT MOVE WITH IT, because the family note tracks it
 * as a separate pass: only the ENDPOINT is carved. The hook
 * `useQueryOrgAnalyticsDashboard` is still unclaimed by this family's `hubs`
 * arm, which is start-anchored on `^(useQueryOrganization|useCombinedOrganization
 * |useCombinedQueriesTeam)` — and `useQueryOrgAnalytics…` matches none of them,
 * for the identical `Org`-vs-`Organization` reason. That is the endpoint-true /
 * symbol-false shape task 2.8 already tracks, and closing it is a hook-grain
 * pass with its own before/after.
 *
 * @domain workspace
 */
export const getOrgAnalyticsDashboard = (organizationId: string) => {
  return http().get<{ url: string }>(
    `/organization/${organizationId}/analytics-dashboard`
  );
};
